// The books. Mallorca: eight climbs set against your own biggest day, sorted how you want,
// each one opening to its note and sources. The Tour: the stages, with what each means to you.
// Every figure is published; where sources disagree, the card says so.
import { useState } from "preact/hooks";
import { useMusette } from "../lib/store";
import { biggestClimb } from "../lib/analysis";
import { dateLabel, daysBetween, todayIso } from "../lib/dates";
import { mallorca, tour, type Stage } from "../data/books";
import { Head } from "../components/Head";
import { photo } from "../data/photos";

const host = (u: string) => new URL(u).hostname.replace(/^www\./, "");
const SORTS = [["Height", "gain"], ["Length", "km"], ["Steepness", "avg"]] as const;

export function Mallorca() {
  const s = useMusette();
  const C = biggestClimb(s.rides)?.m ?? 0;
  const [by, setBy] = useState<"gain" | "km" | "avg">("gain");
  const [open, setOpen] = useState<string | null>(null);
  const climbs = [...mallorca.climbs].sort((a, b) => ((b[by] ?? -1) as number) - ((a[by] ?? -1) as number));
  const maxGain = Math.max(C, ...mallorca.climbs.map((c) => c.gain ?? 0));
  const days = daysBetween(todayIso(), "2027-08-01");

  return (
    <main class="screen signal">
      <Head right={`${days} days`} />
      <section class="stack" style="margin-top: var(--space-3)">
        <div class="card photo-card tall">
          <img src={photo("mallorca")} alt="" decoding="async" />
          <div class="photo-cap">
            <span class="label" style="color:#fff">{mallorca.dates}</span>
            <span class="say" style="color:#fff">Mallorca</span>
            <span style="color:rgba(255,255,255,.85)">Eight climbs, set against your biggest day of {C}{"\u00a0"}m.</span>
          </div>
        </div>

        <div class="card">
          <div class="row-between"><span class="label ink">The climbs</span><span class="label">Published figures</span></div>
          <div class="segmented text" role="tablist">
            {SORTS.map(([l, k]) => <button type="button" role="tab" aria-selected={by === k} onClick={() => setBy(k)}>{l}</button>)}
          </div>
          <div class="climbs">
            {climbs.map((c) => {
              const o = open === c.name;
              return (
                <button type="button" class={`climb${o ? " open" : ""}`} aria-expanded={o} onClick={() => setOpen(o ? null : c.name)}>
                  <div class="row-between"><span class="note-title">{c.name}</span><span class="label">{c.km != null ? `${c.km} km` : ""}{c.avg != null ? ` · ${c.avg}%` : ""}</span></div>
                  <div class="climb-bars" aria-hidden="true">
                    <span class="cb-climb" style={`width:${((c.gain ?? 0) / maxGain) * 100}%`} />
                    <span class="cb-you" style={`left:${(C / maxGain) * 100}%`} />
                  </div>
                  <div class="row-between"><span class="label">{c.gain != null ? `${c.gain} m up` : "Climb unpublished"}</span>{C > 0 && c.gain != null && <span class={`label${c.gain > C ? "" : " amber"}`}>{c.gain > C ? `+${c.gain - C} m on your biggest` : "Within your biggest"}</span>}</div>
                  {o && (
                    <div class="climb-more">
                      <span class="soft">{c.start}{c.top ? `, to ${c.top} m` : ""}{c.max ? `, steepest ${c.max}%` : ""}. {c.note}</span>
                      {c.differs && <span class="label">Sources differ: {c.differs}</span>}
                      <span class="label">{c.src.map(host).join(" · ")}</span>
                    </div>
                  )}
                </button>
              );
            })}
          </div>
          <span class="label">The marker is your biggest climbing day, measured by Strava.</span>
        </div>

        <div class="card">
          <span class="label ink">In August</span>
          {mallorca.practical.map((p) => <div class="note-s"><span class="soft">{p.line}</span><span class="label">{host(p.src)}</span></div>)}
        </div>
        <div class="card row-between"><span class="soft">The routes, planned together in Komoot once you know where you're staying.</span><a class="text-btn small" href="/coach">Ask</a></div>
      </section>
    </main>
  );
}

function StageRow({ st, aside, today }: { st: Stage; aside?: string; today: string }) {
  return (
    <div class={`book-stage${st.date < today ? " past" : ""}`}>
      <div class="row-between"><span class="label">{typeof st.no === "number" ? `Stage ${st.no}` : st.no} · {dateLabel(st.date)}</span><span class="label">{st.km != null ? `${st.km} km` : "Distance unconfirmed"}</span></div>
      <span class="note-title">{st.from} to {st.to}</span>
      {(st.kind || aside) && <span class="soft">{[st.kind, aside].filter(Boolean).join(". ")}</span>}
    </div>
  );
}

export function Tour() {
  const s = useMusette();
  const today = todayIso();
  const liv = s.routes.find((r) => r.id === "liverpool");
  const paris = s.stages[3];
  const [tab, setTab] = useState<"men" | "women">("men");
  return (
    <main class="screen signal">
      <Head right={`${daysBetween(today, "2027-07-02")} days`} />
      <section class="stack" style="margin-top: var(--space-3)">
        <a href="/racing" class="label back">← Race</a>
        <div class="card photo-card tall race-photo">
          <img src={photo("tour")} alt="" decoding="async" />
          <span class="race-stripe" aria-hidden="true" style="--race: var(--race-tour)" />
          <div class="photo-cap">
            <span class="label" style="color:#fff">Femmes through Stockport, 31 July</span>
            <span class="say" style="color:#fff">The Tour, at home</span>
            <span style="color:rgba(255,255,255,.85)">The Femmes leaves Manchester on the A6, through Stockport past the viaduct, then Offerton, Marple and the Goyt Valley.</span>
          </div>
        </div>
        <div class="segmented text" role="tablist">
          <button type="button" role="tab" aria-selected={tab === "men"} onClick={() => setTab("men")}>Men's race</button>
          <button type="button" role="tab" aria-selected={tab === "women"} onClick={() => setTab("women")}>Femmes</button>
        </div>
        {tab === "men" ? (
          <>
            <div class="card">
              <span class="label ink">The UK start</span>
              {tour.men.map((st) => <StageRow today={today} st={st} aside={st.near === "liverpool" && liv ? `Your Liverpool route is ${liv.km} km from home; the finish is a ride away` : undefined} />)}
            </div>
            <div class="card">
              <span class="label ink">The final</span>
              <StageRow today={today} st={tour.final} aside={paris ? `Your own Day 4 finishes in Paris six weeks earlier, ${paris.km.v} km from Dieppe` : undefined} />
            </div>
          </>
        ) : (
          <div class="card">
            <span class="label ink">Tour de France Femmes</span>
            {tour.women.map((st) => <StageRow today={today} st={st} aside={st.near === "stockport" ? "Through Stockport town centre; the council publishes timings later" : st.no === 3 ? "Mallorca starts the same day" : undefined} />)}
          </div>
        )}
        <span class="label">{tour.checked}</span>
      </section>
    </main>
  );
}
